/**
 * Admin table view for listing and filtering spaces (question instances):
 * `<SpacesTable>`.
 *
 * `<SpacesTable>` is config-driven via `SpacesTableConfig`: features push
 * `columns`, `filters`, and an optional `searchEnabled` into the config, and it
 * renders them through `PaginatedTable` with `Space` rows. The server does the
 * filtering, searching and paging: each filter contributes part of the
 * `spaces.list` query, and the table fetches one page of the matching spaces
 * at a time.
 */
import type { Space } from '@repo/shared/orpc/procedures/spaces';
import { PaginatedTable, type TableFilter, usePaginatedTableControls } from 'component/dashboard/paginated-table';
import type { TableColumn } from 'component/dashboard/table-grid';
import { type SpaceListFilter, useSpacesPage } from 'data/spaces';
import type React from 'react';
import { useConfig } from 'util/features-hooks';
import type { TLabel } from 'util/stdtypes';

export type SpaceFilter = TableFilter<SpaceListFilter>;

function spaceKeyOf(space: Space): string {
    return space.spaceKey;
}

export function SpacesTable() {
    const { filters, spaceTableRightWidgets, emptyTableWidget, searchEnabled, tableTitleLabel, columns } = useConfig<SpacesTableConfig>();
    const controls = usePaginatedTableControls({ filters });
    const { spaces, total, isLoading } = useSpacesPage({
        filter: { ...controls.query, name: controls.search },
        pageIndex: controls.pageIndex,
        pageSize: controls.pageSize,
    });
    return (
        <PaginatedTable
            columns={columns}
            controls={controls}
            emptyWidget={emptyTableWidget}
            isLoading={isLoading}
            itemKey={spaceKeyOf}
            items={spaces}
            rightWidgets={spaceTableRightWidgets}
            searchable={searchEnabled}
            titleLabel={tableTitleLabel}
            totalCount={total}
        />
    );
}

export type SpacesTableConfig = {
    spaceTableRightWidgets: React.FunctionComponent[];
    filters: SpaceFilter[];
    searchEnabled?: boolean;
    columns: TableColumn<Space>[];
    emptyTableWidget?: React.ComponentType;
    tableTitleLabel: TLabel;
};
